import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://5vix.com';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '5vix',
  url: siteUrl,
  description: 'Integrated gym management system for membership, scheduling, billing, attendance, and analytics.',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+201044553254',
    contactType: 'customer support',
    availableLanguage: ['English', 'Arabic'],
  },
  sameAs: [
    'https://www.instagram.com/5vixofficial/',
    'https://www.tiktok.com/@5vixofficial',
    'https://www.youtube.com/@5vix-c3p',
    'https://www.linkedin.com/in/5vix-software-12058b363/',
  ],
};

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  const pageJsonLd = [
    organizationSchema,
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: '5vix',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web, iOS, Android',
      description: 'Integrated gym management platform for membership, scheduling, billing, attendance, and analytics.',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: '14-day free trial',
      },
    },
  ];

  return (
    <div className="bg-background-50 min-h-screen">
      <JsonLd data={pageJsonLd} />
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] flex items-center overflow-hidden bg-gradient-to-br from-primary-950 via-primary-900 to-primary-800">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary-400/20 via-transparent to-transparent"></div>

        <div className="relative z-10 w-full px-4 md:px-6 py-20 md:py-28">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="w-full lg:w-1/2 text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-6 leading-tight">
                {t('hero.title1')}{' '}
                <span className="text-primary-400">{t('hero.title2')}</span>
              </h1>
              <p className="text-background-50/80 text-base md:text-lg mb-8 max-w-xl leading-relaxed">
                {t('hero.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/#register"
                  className="bg-primary-500 text-background-50 px-8 py-3.5 rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm inline-flex items-center justify-center"
                >
                  {t('hero.tryFree')}
                </Link>
                <a
                  href="#demo"
                  className="border-2 border-background-50/40 text-background-50 px-8 py-3.5 rounded-lg hover:bg-background-50/10 hover:border-background-50/60 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm inline-flex items-center justify-center"
                >
                  {t('hero.requestDemo')}
                </a>
              </div>
            </div>

            <div className="w-full lg:w-1/2">
              <div className="bg-background-50/95 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-background-200/50">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-3xl font-heading font-bold text-foreground-950">86,000</h2>
                    <p className="text-foreground-600 text-sm">{t('hero.revenue')}</p>
                  </div>
                  <div className="text-red-500 text-sm font-bold">
                    -71.0% <span className="text-foreground-500 font-normal">{t('hero.fromLastMonth') || 'from last month'}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-foreground-950">952</h3>
                    <p className="text-foreground-600 text-sm">{t('hero.totalMemberships')}</p>
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-foreground-600">{t('hero.active')}: 520</span>
                        <span className="text-foreground-600">{t('hero.inactive')}: 432</span>
                      </div>
                      <div className="h-2 bg-background-200 rounded-full overflow-hidden">
                        <div className="h-full bg-primary-500 rounded-full" style={{ width: '55%' }}></div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-foreground-950">26</h3>
                    <p className="text-foreground-600 text-sm">{t('hero.newMemberships')}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex -space-x-2">
                        {[1, 2].map((i) => (
                          <div key={i} className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <i className="ri-user-add-line text-primary-600 text-sm"></i>
                          </div>
                        ))}
                      </div>
                      <span className="text-xs text-foreground-500">{t('hero.thisMonth')}</span>
                    </div>
                  </div>
                </div>

                <div className="relative h-32">
                  <div className="absolute inset-0 flex items-end justify-between gap-1.5">
                    {[40, 60, 50, 70, 45, 80, 55].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 bg-primary-200 rounded-t-sm transition-all"
                        style={{ height: `${h}%` }}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                {t('whyChoose.title')}
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-base">
                {t('whyChoose.description')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: 'ri-time-line', title: t('whyChoose.timeSaving'), desc: t('whyChoose.timeSavingDesc') },
                { icon: 'ri-bar-chart-line', title: t('whyChoose.analytics'), desc: t('whyChoose.analyticsDesc') },
                { icon: 'ri-customer-service-line', title: t('whyChoose.experience'), desc: t('whyChoose.experienceDesc') },
              ].map((item, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-8 text-center hover:border-primary-300 transition-all cursor-default">
                  <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <i className={`${item.icon} text-primary-600 text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-3">{item.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Services Section */}
      <ScrollReveal>
        <section id="services" className="py-16 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                {t('services.title')}
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-base">
                {t('services.description')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-user-add-line', title: t('services.membership.title'), desc: t('services.membership.description'), features: [t('services.membership.feature1'), t('services.membership.feature2'), t('services.membership.feature3')] },
                { icon: 'ri-calendar-check-line', title: t('services.scheduling.title'), desc: t('services.scheduling.description'), features: [t('services.scheduling.feature1'), t('services.scheduling.feature2'), t('services.scheduling.feature3')] },
                { icon: 'ri-money-dollar-circle-line', title: t('services.payment.title'), desc: t('services.payment.description'), features: [t('services.payment.feature1'), t('services.payment.feature2'), t('services.payment.feature3')] },
                { icon: 'ri-pie-chart-line', title: t('services.analytics.title'), desc: t('services.analytics.description'), features: [t('services.analytics.feature1'), t('services.analytics.feature2'), t('services.analytics.feature3')] },
                { icon: 'ri-smartphone-line', title: t('services.memberApp.title'), desc: t('services.memberApp.description'), features: [t('services.memberApp.feature1'), t('services.memberApp.feature2'), t('services.memberApp.feature3')] },
                { icon: 'ri-store-line', title: t('services.inventory.title'), desc: t('services.inventory.description'), features: [t('services.inventory.feature1'), t('services.inventory.feature2'), t('services.inventory.feature3')] },
              ].map((service, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-6 border border-background-200/70 hover:border-primary-300 transition-all">
                  <div className="w-11 h-11 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">{service.title}</h3>
                  <p className="text-foreground-600 text-sm mb-4 leading-relaxed">{service.desc}</p>
                  <ul className="space-y-2">
                    {service.features.map((f, j) => (
                      <li key={j} className="flex items-center text-sm text-foreground-700">
                        <div className="w-4 h-4 flex items-center justify-center text-primary-500 mr-2 rtl:mr-0 rtl:ml-2 shrink-0">
                          <i className="ri-check-line"></i>
                        </div>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Statistics Section */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-foreground-950">86,000</h3>
                    <p className="text-foreground-600 text-sm">{t('stats.revenue')}</p>
                  </div>
                  <div className="text-red-500 text-xs font-bold">
                    -71.0% <span className="text-foreground-500 font-normal">{t('stats.fromPreviousMonth')}</span>
                  </div>
                </div>
                <div className="relative h-28">
                  <div className="absolute inset-0 flex items-end justify-between gap-1">
                    {[30, 45, 35, 60, 40, 70, 50, 55, 45, 65, 50, 60].map((h, i) => (
                      <div key={i} className="flex-1 bg-primary-100 rounded-t-sm" style={{ height: `${h}%` }}></div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-foreground-950">9,600</h3>
                    <p className="text-foreground-600 text-sm">{t('stats.netProfit')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-20 bg-background-200 rounded-full overflow-hidden">
                      <div className="h-full bg-primary-500 rounded-full" style={{ width: '65%' }}></div>
                    </div>
                    <span className="text-xs text-foreground-500">65%</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-foreground-600">{t('stats.income')}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 bg-primary-100 rounded-full overflow-hidden">
                        <div className="h-full bg-primary-500 rounded-full" style={{ width: '80%' }}></div>
                      </div>
                      <span className="text-foreground-800 font-medium">86,000</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-foreground-600">{t('stats.expenses')}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 bg-accent-100 rounded-full overflow-hidden">
                        <div className="h-full bg-accent-500 rounded-full" style={{ width: '70%' }}></div>
                      </div>
                      <span className="text-foreground-800 font-medium">76,400</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-foreground-950">952</h3>
                    <p className="text-foreground-600 text-sm">{t('stats.members')}</p>
                  </div>
                  <div className="flex -space-x-2">
                    {[1, 2].map((i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                        <i className="ri-user-add-line text-accent-600 text-sm"></i>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-600">{t('hero.active')}: 520</span>
                    <span className="text-foreground-600">{t('hero.inactive')}: 432</span>
                  </div>
                  <div className="h-2 bg-background-200 rounded-full overflow-hidden">
                    <div className="h-full bg-accent-500 rounded-full" style={{ width: '55%' }}></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">{t('stats.organicSources')}</h3>
                <div className="space-y-4">
                  {[
                    { label: t('stats.socialMedia'), pct: '41.4%', val: '22.5K', color: 'bg-primary-500' },
                    { label: t('stats.friends'), pct: '28.7%', val: '20.2K', color: 'bg-accent-500' },
                    { label: t('stats.directVisit'), pct: '26.9%', val: '24.1K', color: 'bg-secondary-500' },
                    { label: t('stats.calls'), pct: '3.0%', val: '2.6K', color: 'bg-foreground-300' },
                  ].map((s, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 ${s.color} rounded-full`}></div>
                        <span className="text-foreground-700 text-sm">{s.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-900 font-semibold text-sm">{s.pct}</span>
                        <span className="text-foreground-400 text-xs">{s.val}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">{t('stats.billingStatus')}</h3>
                <div className="grid grid-cols-5 gap-3">
                  {[
                    { label: t('stats.draft'), value: '12', color: 'bg-foreground-200 text-foreground-700' },
                    { label: t('stats.sent'), value: '18', color: 'bg-accent-100 text-accent-700' },
                    { label: t('stats.paid'), value: '45', color: 'bg-secondary-100 text-secondary-700' },
                    { label: t('stats.partiallyPaid'), value: '7', color: 'bg-primary-100 text-primary-700' },
                    { label: t('stats.overdue'), value: '3', color: 'bg-red-100 text-red-600' },
                  ].map((item, i) => (
                    <div key={i} className="text-center">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-2 bg-background-100">
                        <span className="font-heading font-bold text-lg text-foreground-950">{item.value}</span>
                      </div>
                      <p className="text-xs text-foreground-600 leading-tight">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Registration Section */}
      <ScrollReveal>
        <section id="register" className="py-16 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
              <div className="w-full lg:w-1/2">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-6">
                  {t('register.title1')}{' '}
                  <span className="text-primary-500">{t('register.title2')}</span>{' '}
                  {t('register.title3')}
                </h2>
                <p className="text-foreground-600 mb-8 text-base leading-relaxed">
                  {t('register.description')}
                </p>
                <div className="space-y-6">
                  {[
                    { icon: 'ri-rocket-line', title: t('register.quickSetup'), desc: t('register.quickSetupDesc') },
                    { icon: 'ri-shield-check-line', title: t('register.dataSafe'), desc: t('register.dataSafeDesc') },
                    { icon: 'ri-customer-service-2-line', title: t('register.support'), desc: t('register.supportDesc') },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mt-0.5 mr-4 rtl:mr-0 rtl:ml-4 shrink-0">
                        <i className={`${item.icon} text-primary-600`}></i>
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-foreground-950 mb-1">{item.title}</h3>
                        <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="w-full lg:w-1/2">
                <div className="bg-background-50 rounded-2xl p-8 border border-background-200/70">
                  <h3 className="text-2xl font-heading font-bold text-foreground-950 mb-6">{t('register.formTitle')}</h3>
                  <form className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t('register.firstName')}
                        </label>
                        <input
                          type="text" id="firstName" name="firstName" required
                          className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent text-foreground-950 placeholder:text-foreground-400"
                          placeholder={t('register.firstName')}
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t('register.lastName')}
                        </label>
                        <input
                          type="text" id="lastName" name="lastName" required
                          className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent text-foreground-950 placeholder:text-foreground-400"
                          placeholder={t('register.lastName')}
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="gymName" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t('register.gymName')}
                      </label>
                      <input
                        type="text" id="gymName" name="gymName" required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent text-foreground-950 placeholder:text-foreground-400"
                        placeholder={t('register.gymName')}
                      />
                    </div>
                    <div>
                      <label htmlFor="package" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t('register.package')}
                      </label>
                      <select
                        id="package" name="package" required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent text-foreground-950 cursor-pointer"
                      >
                        <option value="">{t('register.selectPackage')}</option>
                        <option value="basic">{t('register.basic')}</option>
                        <option value="professional">{t('register.professional')}</option>
                        <option value="integrated">{t('register.integrated')}</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => window.open('https://wa.me/+201044553254?text=Hello%205vix!%20I%20want%20to%20start%20my%20free%20trial.', '_blank')}
                      className="w-full bg-primary-500 text-background-50 py-3 rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm flex items-center justify-center gap-2"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-whatsapp-line"></i>
                      </div>
                      {t('register.createAccount')}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Testimonials Section */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                {t('testimonials.title')}
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-base">
                {t('testimonials.description')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-4 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-6">
                    {t(`testimonials.customer${n}.text`)}
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-primary-600"></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground-950 text-sm">
                        {t(`testimonials.customer${n}.name`)}
                      </h4>
                      <p className="text-foreground-500 text-xs">{t(`testimonials.customer${n}.role`)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Blog Preview Section */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                {t('blog.title')}
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-base">
                {t('blog.description')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <Link
                  key={n}
                  to="/blog"
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={
                        n === 1 ? 'https://readdy.ai/api/search-image?query=Modern%20fitness%20gym%20training%20scene%20with%20professional%20athletic%20atmosphere%20clean%20minimalist%20design%20warm%20coral%20and%20terracotta%20accents%20soft%20natural%20lighting%20editorial%20quality%20composition%20no%20text%20overlay&width=600&height=400&seq=blog-preview-1&orientation=landscape' :
                        n === 2 ? 'https://readdy.ai/api/search-image?query=Fitness%20nutrition%20meal%20planning%20scene%20with%20fresh%20healthy%20ingredients%20arranged%20on%20clean%20surface%20minimalist%20design%20warm%20lighting%20professional%20food%20photography%20editorial%20quality&width=600&height=400&seq=blog-preview-2&orientation=landscape' :
                        'https://readdy.ai/api/search-image?query=Gym%20data%20analytics%20business%20dashboard%20displayed%20on%20laptop%20with%20charts%20and%20graphs%20modern%20workspace%20warm%20minimalist%20aesthetic%20clean%20composition%20professional%20editorial%20photography&width=600&height=400&seq=blog-preview-3&orientation=landscape'
                      }
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      alt={t(`blog.article${n}.title`)}
                      title={t(`blog.article${n}.title`)}
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-4 text-xs text-foreground-400 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        May {13 - (n - 1) * 4}, 2025
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors text-base">
                      {t(`blog.article${n}.title`)}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed line-clamp-2 mb-4">
                      {t(`blog.article${n}.description`)}
                    </p>
                    <span className="text-primary-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      {t('blog.readMore')}
                      <i className={`ri-arrow-right-line ${isRTL ? 'rotate-180' : ''}`}></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/blog"
                className="inline-flex items-center border-2 border-primary-500 text-primary-600 px-8 py-3 rounded-lg hover:bg-primary-500 hover:text-background-50 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm"
              >
                {t('blog.viewAll')}
              </Link>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA Section */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-primary-500">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-background-50/85 text-base md:text-lg mb-8 leading-relaxed">
              {t('cta.description')}
            </p>
            <Link
              to="/#register"
              className="inline-block bg-background-50 text-primary-600 px-10 py-3.5 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm"
            >
              {t('cta.button')}
            </Link>
          </div>
        </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
}